/**
 * ErrorCaptureProvider — captures all frontend errors and reports to Ops Center
 *
 * Ported from PSA v1 GlobalErrorCaptureContext. Zero platform-specific imports.
 *
 * Captures: window.onerror, unhandledrejection, console.error, fetch wrapping
 * Batching: 2s interval, max 10/batch, max 50 queue
 * Deduplication by fingerprint within batch
 * sendBeacon on page unload
 */

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useCallback,
  useState,
  type ReactNode,
} from 'react';
import { OpsErrorClient } from './ops-error-client';
import { generateErrorFingerprint, getBrowserInfo, sanitizePayload, detectErrorCategory, detectErrorSeverity } from './error-fingerprint';
import type { ErrorConfig, ErrorUser, ErrorCaptureContextValue, BrowserErrorPayload, ErrorSource } from './types';

const CONFIG = {
  BATCH_INTERVAL: 2000,
  MAX_BATCH_SIZE: 10,
  MAX_QUEUE_SIZE: 50,
  IGNORED_PATTERNS: [
    /ResizeObserver loop/i,
    /Script error\.$/i,
    /Loading chunk .+ failed/i,
    /ChunkLoadError/i,
    /Failed to fetch dynamically imported module/i,
    /cancelled/i,
    /AbortError/i,
    /Invalid email or password/i,
    /Invalid credentials/i,
    /Account temporarily locked/i,
    /Too many login attempts/i,
    /CAPTCHA verification required/i,
    /session.*expired/i,
    /HTTP 401:/i,
    /HTTP 429:/i,
  ],
  SKIP_URLS: ['/api/errors/ingest', 'errors/ingest'],
  SENSITIVE_FIELDS: ['password', 'token', 'apikey', 'api_key', 'secret', 'credential', 'authorization'],
};

interface QueuedError {
  payload: BrowserErrorPayload;
  fingerprint: string;
}

const ErrorCaptureContext = createContext<ErrorCaptureContextValue>({
  errorCount: 0,
  reportedCount: 0,
  reportError: () => {},
  isActive: false,
  flushNow: () => {},
});

function shouldIgnoreError(message: string): boolean {
  return CONFIG.IGNORED_PATTERNS.some(pattern => pattern.test(message));
}

function shouldSkipUrl(url: string): boolean {
  return CONFIG.SKIP_URLS.some(pattern => url.includes(pattern));
}

function extractErrorDetails(error: unknown): { message: string; stack?: string; code?: string } {
  if (error instanceof Error) {
    return { message: error.message || 'Unknown error', stack: error.stack, code: (error as { code?: string }).code };
  }
  if (typeof error === 'string') return { message: error };
  if (error && typeof error === 'object') {
    const e = error as Record<string, unknown>;
    return {
      message: String(e.message || e.error || e.reason || 'Unknown error'),
      stack: String(e.stack || ''),
      code: String(e.code || ''),
    };
  }
  return { message: 'Unknown error' };
}

interface ErrorCaptureProviderProps {
  config: ErrorConfig;
  user?: ErrorUser;
  children: ReactNode;
}

export function ErrorCaptureProvider({ config, user, children }: ErrorCaptureProviderProps) {
  const [errorCount, setErrorCount] = useState(0);
  const [reportedCount, setReportedCount] = useState(0);

  const clientRef = useRef(new OpsErrorClient(config));
  const errorQueueRef = useRef<QueuedError[]>([]);
  const batchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isSendingRef = useRef(false);
  const userRef = useRef(user);

  // Keep user ref updated
  useEffect(() => { userRef.current = user; }, [user]);

  // Update client if config changes
  useEffect(() => {
    clientRef.current = new OpsErrorClient(config);
  }, [config.apiBaseUrl, config.apiKey, config.platformId]);

  // Flush error queue
  const flushErrorQueue = useCallback(async () => {
    if (isSendingRef.current || errorQueueRef.current.length === 0) return;
    isSendingRef.current = true;

    const toSend = errorQueueRef.current.map(e => e.payload);
    errorQueueRef.current = [];

    try {
      const result = await clientRef.current.reportBatch(toSend);
      setReportedCount(c => c + result.processed);
    } catch {
      // Silently fail — don't crash the app because error reporting failed
    } finally {
      isSendingRef.current = false;
    }
  }, []);

  // Queue an error
  const queueError = useCallback(
    (source: ErrorSource, action: string, message: string, details: {
      stack?: string; code?: string; url?: string; method?: string; metadata?: Record<string, unknown>;
    } = {}) => {
      try {
        if (shouldIgnoreError(message)) return;

        const fingerprint = generateErrorFingerprint({
          errorMessage: message,
          actionAttempted: action,
          requestUrl: details.url,
          errorCode: details.code,
        });

        // Deduplicate within queue
        if (errorQueueRef.current.some(e => e.fingerprint === fingerprint)) return;

        const browserInfo = getBrowserInfo();
        const currentUser = userRef.current;
        const statusCode = details.code ? parseInt(details.code, 10) : undefined;
        const category = detectErrorCategory(message, isNaN(statusCode!) ? undefined : statusCode);
        const severity = detectErrorSeverity(message, category);

        const payload: BrowserErrorPayload = {
          error_message: message,
          action_attempted: action,
          error_code: details.code,
          stack_trace: details.stack,
          request_url: details.url,
          request_method: details.method,
          current_route: typeof window !== 'undefined' ? window.location.pathname : undefined,
          source,
          category,
          severity,
          browser_info: browserInfo,
          user_id: currentUser?.id,
          user_email: currentUser?.email,
          system_data: details.metadata ? sanitizePayload(details.metadata) as Record<string, unknown> : undefined,
        };

        errorQueueRef.current.push({ payload, fingerprint });
        setErrorCount(c => c + 1);

        // Trim queue
        if (errorQueueRef.current.length > CONFIG.MAX_QUEUE_SIZE) {
          errorQueueRef.current = errorQueueRef.current.slice(-CONFIG.MAX_QUEUE_SIZE);
        }

        // Schedule or force flush
        if (!batchTimeoutRef.current) {
          batchTimeoutRef.current = setTimeout(() => {
            batchTimeoutRef.current = null;
            flushErrorQueue();
          }, CONFIG.BATCH_INTERVAL);
        }

        if (errorQueueRef.current.length >= CONFIG.MAX_BATCH_SIZE) {
          if (batchTimeoutRef.current) {
            clearTimeout(batchTimeoutRef.current);
            batchTimeoutRef.current = null;
          }
          flushErrorQueue();
        }
      } catch {
        // Never crash due to error reporting
      }
    },
    [flushErrorQueue]
  );

  // Manual error reporting
  const reportError = useCallback(
    (error: Error | string, action: string, metadata?: Record<string, unknown>) => {
      const details = extractErrorDetails(error);
      queueError('user_reported', action, details.message, {
        stack: details.stack,
        code: details.code,
        metadata,
      });
    },
    [queueError]
  );

  // Install global error handlers
  useEffect(() => {
    const originalConsoleError = console.error;
    let isIntercepting = false;

    const handleWindowError = (event: ErrorEvent) => {
      const { message, filename, lineno, colno, error } = event;
      const fullMessage = `${message} at ${filename}:${lineno}:${colno}`;
      queueError('runtime_error', 'Uncaught JavaScript Error', fullMessage, {
        stack: error?.stack,
        url: filename,
        metadata: { lineno, colno, filename },
      });
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const details = extractErrorDetails(event.reason);
      queueError('promise_rejection', 'Unhandled Promise Rejection', details.message, {
        stack: details.stack,
        code: details.code,
      });
    };

    const interceptedConsoleError = (...args: unknown[]) => {
      originalConsoleError.apply(console, args);
      if (isIntercepting) return;
      isIntercepting = true;

      try {
        const firstArg = args[0];
        if (typeof firstArg === 'string' && firstArg.startsWith('[ErrorCapture]')) return;

        const message = args.map(arg => {
          if (arg instanceof Error) return arg.message;
          if (typeof arg === 'string') return arg;
          try { return JSON.stringify(arg); } catch { return String(arg); }
        }).join(' ');

        const errorObj = args.find(arg => arg instanceof Error) as Error | undefined;
        queueError('console_error', 'Console Error', message, {
          stack: errorObj?.stack,
          code: (errorObj as { code?: string } | undefined)?.code,
        });
      } finally {
        isIntercepting = false;
      }
    };

    // Wrap fetch for network error capture
    const currentFetch = window.fetch;
    const wrappedFetch: typeof fetch = async (input, init) => {
      const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
      const method = init?.method || 'GET';

      if (shouldSkipUrl(url)) {
        return currentFetch(input, init);
      }

      try {
        const response = await currentFetch(input, init);
        if (!response.ok && response.status !== 401 && response.status !== 404 && response.status !== 429) {
          let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
          try {
            const clone = response.clone();
            const body = await clone.json();
            if (body.error || body.message) {
              errorMessage = `${errorMessage} - ${body.error || body.message}`;
            }
          } catch { /* ignore parse errors */ }

          queueError('api_error', `API Error: ${method} ${url}`, errorMessage, {
            code: String(response.status), url, method,
          });
        }
        return response;
      } catch (error) {
        const details = extractErrorDetails(error);
        if (details.message.toLowerCase().includes('abort')) throw error;
        queueError('network_error', `Network Error: ${method} ${url}`, details.message, {
          stack: details.stack, url, method,
        });
        throw error;
      }
    };

    window.addEventListener('error', handleWindowError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    console.error = interceptedConsoleError;
    window.fetch = wrappedFetch;

    return () => {
      window.removeEventListener('error', handleWindowError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      console.error = originalConsoleError;
      window.fetch = currentFetch;
      if (batchTimeoutRef.current) clearTimeout(batchTimeoutRef.current);
    };
  }, [queueError]);

  // sendBeacon on page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (errorQueueRef.current.length > 0) {
        const payloads = errorQueueRef.current.map(e => e.payload);
        clientRef.current.sendBeaconBatch(payloads);
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  return (
    <ErrorCaptureContext.Provider
      value={{ errorCount, reportedCount, reportError, isActive: true, flushNow: flushErrorQueue }}
    >
      {children}
    </ErrorCaptureContext.Provider>
  );
}

export function useErrorCapture(): ErrorCaptureContextValue {
  return useContext(ErrorCaptureContext);
}
